import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, RefreshCw, Search, Users, X } from "lucide-react";
import { billsRead, isFeatureOff } from "@/lib/billsNative";
import { useSession } from "@/contexts/SessionContext";

const money = (v, c = "INR") =>
  v == null ? "—" : `${c === "INR" ? "₹" : `${c} `}${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function VendorDirectory() {
  const { currentOutletId } = useSession();
  const [q, setQ] = useState("");
  const [state, setState] = useState({ phase: "loading", items: [], total: 0 });

  const load = useCallback(async (term) => {
    if (!currentOutletId) return setState({ phase: "no-outlet", items: [], total: 0 });
    setState((s) => ({ ...s, phase: s.items.length ? "refreshing" : "loading" }));
    const r = await billsRead.vendors({ q: term || undefined, limit: 200 });
    if (isFeatureOff(r) || (!r.ok && (r.status === 503 || r.status === 0)))
      return setState({ phase: "unavailable", items: [], total: 0 });
    if (!r.ok) return setState({ phase: "error", items: [], total: 0 });
    setState({ phase: "ready", items: r.data.items, total: r.data.total });
  }, [currentOutletId]);

  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), q ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="space-y-8 pb-16" data-testid="vendor-directory">
      <header className="space-y-1">
        <h1 className="text-4xl sm:text-5xl font-semibold text-archival-primary tracking-tight leading-[1.05]">
          Vendors
        </h1>
        <p className="text-base md:text-lg text-archival-secondary">
          Who you buy from, and what you have bought from them.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-archival-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search vendor name or GSTIN"
            data-testid="vendor-search-input"
            className="w-full h-11 pl-9 pr-9 rounded-md border border-archival bg-archival-surface text-[14px] text-archival-primary placeholder:text-archival-muted focus:outline-none focus:ring-2 focus:ring-archival-primary/20"
          />
          {q && (
            <button type="button" onClick={() => setQ("")} aria-label="Clear search" data-testid="vendor-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-archival-muted hover:text-archival-primary">
              <X size={14} />
            </button>
          )}
        </div>
        <Link to="/vendors/resolve" data-testid="vendor-needs-resolution"
          className="text-[13px] font-medium text-archival-primary underline underline-offset-4">
          Needs resolution
        </Link>
      </div>

      {state.phase === "no-outlet" && (
        <p className="text-[15px] text-archival-secondary" data-testid="vendor-no-outlet">
          Select an outlet to see vendor purchase history.
        </p>
      )}

      {state.phase === "loading" && (
        <div className="flex items-center gap-2 text-archival-secondary text-[14px]" data-testid="vendor-loading">
          <Loader2 className="animate-spin" size={16} /> Loading vendors…
        </div>
      )}

      {(state.phase === "unavailable" || state.phase === "error") && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-6 max-w-xl" data-testid="vendor-unavailable">
          <div className="flex items-center gap-2 text-amber-900">
            <AlertTriangle size={16} />
            <h2 className="text-base font-semibold">Vendor history is temporarily unavailable</h2>
          </div>
          <p className="mt-2 text-[13px] text-amber-900/90">
            We couldn&apos;t reach the Bills datastore. No vendor or bill data has been changed.
          </p>
          <button type="button" onClick={() => load(q.trim())} data-testid="vendor-retry"
            className="mt-4 inline-flex items-center gap-1.5 rounded px-3 py-2 text-[13px] font-medium border border-amber-300 hover:bg-amber-100 transition-colors">
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      )}

      {["ready", "refreshing"].includes(state.phase) && (
        state.items.length === 0 ? (
          <div className="rounded-md border border-dashed border-archival p-10 text-center" data-testid="vendor-empty">
            <Users size={20} className="mx-auto text-archival-muted" />
            <p className="mt-2 text-[14px] font-medium text-archival-primary">
              {q ? "No vendors match your search" : "No vendor purchase history yet"}
            </p>
            <p className="text-[13px] text-archival-secondary">
              {q ? "Try a different name or GSTIN." : "Vendors appear here once their bills are confirmed."}
            </p>
          </div>
        ) : (
          <>
            <p className="text-[12px] text-archival-muted" data-testid="vendor-count">
              {state.total} {state.total === 1 ? "vendor" : "vendors"}
            </p>
            <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="vendor-list">
              {state.items.map((v) => (
                <li key={v.vendor_id}>
                  <Link to={`/vendors/${encodeURIComponent(v.vendor_id)}`} data-testid={`vendor-card-${v.vendor_id}`}
                    className="block rounded-md border border-archival bg-archival-surface px-4 py-4 h-full hover:bg-archival-base transition-colors">
                    <p className="text-[15px] font-medium text-archival-primary truncate">{v.vendor_name}</p>
                    <p className="text-[12px] text-archival-muted truncate mt-0.5">
                      {v.gstin || "No GSTIN on file"}
                    </p>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
                      <div>
                        <dt className="text-archival-muted">Purchases</dt>
                        <dd className="font-medium text-archival-primary tabular-nums">{money(v.purchase_value)}</dd>
                      </div>
                      <div>
                        <dt className="text-archival-muted">Bills</dt>
                        <dd className="font-medium text-archival-primary tabular-nums">{v.bills}</dd>
                      </div>
                      <div className="col-span-2">
                        <dt className="text-archival-muted">Last purchase</dt>
                        <dd className="font-medium text-archival-primary">{v.last_purchase || "—"}</dd>
                      </div>
                    </dl>
                  </Link>
                </li>
              ))}
            </ul>
          </>
        )
      )}
    </div>
  );
}
